import type { SavedRoofModulesLayout } from '../../../../../../@types/types';
import type { AiDesignsDataSource } from '../sections/DashboardAiDesignsSourceBadge';
import { countModulesInLayout } from './aiDesignFlowDebug';
import {
    candidateLayoutSignature,
    physicalLayoutSignature,
    type CategoryLayoutCandidate,
} from './categoryBestVariantScoring';
import { canonicalCategoryName } from './resolveRoofCombinationsForRunAi';

export type AltLayoutVariantRow = {
    id?: string;
    label?: string;
    roofIds?: string[];
    dir?: unknown;
    minEff?: number | null;
    productionW?: number;
    modules?: number;
    modulesLayout?: SavedRoofModulesLayout;
    efficiency?: number | null;
    solarAccess?: number | null;
    multiplier?: number | null;
};

function asPositiveNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return 0;
}

/** Positive scalar for display; treats zero as missing. */
export function positiveLayoutMetric(value: unknown): number | undefined {
    const parsed = asPositiveNumber(value);
    return parsed > 0 ? parsed : undefined;
}

function hasModulesLayout(layout: SavedRoofModulesLayout | undefined): layout is SavedRoofModulesLayout {
    return Array.isArray(layout) && layout.length > 0;
}

function asOptionalMetric(value: unknown): number | null | undefined {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined;
    return value;
}

/** Stable layout-map key for a category More Options variant row. */
export function resolveAltLayoutVariantLayoutKey(
    category: string,
    variant: Pick<AltLayoutVariantRow, 'id' | 'label' | 'modules'>,
): string {
    return `variant:${category}:${variant.id ?? variant.label ?? variant.modules ?? 'unknown'}`;
}

type ResolveAltLayoutVariantMetricsArgs = {
    category: string | null | undefined;
    variant: AltLayoutVariantRow;
    layoutModulesLayoutByKey?: Record<string, SavedRoofModulesLayout | undefined>;
    layoutProductionByKey?: Record<string, number | undefined>;
    layoutModulesByKey?: Record<string, number | undefined>;
    layoutMetricsByKey?: Record<string, unknown>;
};

/** Merge scalar metrics and module placements from variant rows and rebuilt layout maps. */
export function resolveAltLayoutVariantMetrics(
    args: ResolveAltLayoutVariantMetricsArgs,
): AltLayoutVariantRow {
    const {
        category,
        variant,
        layoutModulesLayoutByKey,
        layoutProductionByKey,
        layoutModulesByKey,
        layoutMetricsByKey,
    } = args;

    const { modulesLayout: _inlineLayout, ...variantRest } = variant;
    const variantKey = category ? resolveAltLayoutVariantLayoutKey(category, variant) : undefined;
    const modulesLayoutFromMap = variantKey ? layoutModulesLayoutByKey?.[variantKey] : undefined;
    const modulesLayout = hasModulesLayout(variant.modulesLayout)
        ? variant.modulesLayout
        : hasModulesLayout(modulesLayoutFromMap)
          ? modulesLayoutFromMap
          : undefined;

    const layoutModuleCount = countModulesInLayout(modulesLayout);
    const modules =
        asPositiveNumber(variant.modules) ||
        layoutModuleCount ||
        (variantKey ? asPositiveNumber(layoutModulesByKey?.[variantKey]) : 0);

    const productionW =
        asPositiveNumber(variant.productionW) ||
        (variantKey ? asPositiveNumber(layoutProductionByKey?.[variantKey]) : 0);

    const metrics = (variantKey ? layoutMetricsByKey?.[variantKey] : undefined) as
        | {
              efficiency?: number | null;
              solarAccess?: number | null;
              multiplier?: number | null;
          }
        | undefined;

    const efficiency =
        asOptionalMetric(variant.efficiency) ??
        asOptionalMetric(metrics?.efficiency) ??
        (variant.efficiency == null ? null : variant.efficiency);
    const solarAccess =
        asOptionalMetric(variant.solarAccess) ??
        asOptionalMetric(metrics?.solarAccess) ??
        (variant.solarAccess == null ? null : variant.solarAccess);
    const multiplier =
        asOptionalMetric(variant.multiplier) ??
        asOptionalMetric(metrics?.multiplier) ??
        (variant.multiplier == null ? null : variant.multiplier);

    return {
        ...variantRest,
        ...(hasModulesLayout(modulesLayout) ? { modulesLayout } : {}),
        ...(modules > 0 ? { modules } : {}),
        ...(productionW > 0 ? { productionW } : {}),
        efficiency: efficiency ?? null,
        solarAccess: solarAccess ?? null,
        multiplier: multiplier ?? null,
    };
}

function variantToCandidate(category: string, variant: AltLayoutVariantRow): CategoryLayoutCandidate {
    const roofIds = Array.isArray(variant.roofIds) ? variant.roofIds.filter(Boolean) : [];
    const modules = asPositiveNumber(variant.modules) || countModulesInLayout(variant.modulesLayout);
    const production = asPositiveNumber(variant.productionW);
    return {
        key: resolveAltLayoutVariantLayoutKey(category, variant),
        source: 'variant',
        variantId: typeof variant.id === 'string' ? variant.id : undefined,
        label: typeof variant.label === 'string' ? variant.label : undefined,
        roofIds,
        dir: variant.dir === 'port' || variant.dir === 'land' || variant.dir === 'both' ? variant.dir : null,
        minEff: typeof variant.minEff === 'number' && Number.isFinite(variant.minEff) ? variant.minEff : null,
        production,
        modules,
        efficiency:
            typeof variant.efficiency === 'number' && Number.isFinite(variant.efficiency) ? variant.efficiency : null,
        solarAccess:
            typeof variant.solarAccess === 'number' && Number.isFinite(variant.solarAccess)
                ? variant.solarAccess
                : null,
        multiplier:
            typeof variant.multiplier === 'number' && Number.isFinite(variant.multiplier)
                ? variant.multiplier
                : null,
    };
}

/** Resolve cached variant rows even when the category label differs slightly from blob keys. */
export function resolveVariantsForCategory(
    category: string | null | undefined,
    byCategory: Record<string, unknown[]>,
): AltLayoutVariantRow[] {
    if (!category) return [];
    const direct = byCategory[category];
    if (Array.isArray(direct) && direct.length > 0) {
        return direct as AltLayoutVariantRow[];
    }
    const target = canonicalCategoryName(category);
    for (const [key, rows] of Object.entries(byCategory)) {
        if (canonicalCategoryName(key) === target && Array.isArray(rows) && rows.length > 0) {
            return rows as AltLayoutVariantRow[];
        }
    }
    return Array.isArray(direct) ? (direct as AltLayoutVariantRow[]) : [];
}

function resolveCategoryMain(
    category: string | null | undefined,
    categoryMainLayoutByCategory: Record<string, CategoryLayoutCandidate>,
): CategoryLayoutCandidate | undefined {
    if (!category) return undefined;
    const direct = categoryMainLayoutByCategory[category];
    if (direct) return direct;
    const target = canonicalCategoryName(category);
    for (const [key, candidate] of Object.entries(categoryMainLayoutByCategory)) {
        if (canonicalCategoryName(key) === target) return candidate;
    }
    return undefined;
}

function hasLayoutQuantityData(variant: AltLayoutVariantRow): boolean {
    const modules = asPositiveNumber(variant.modules);
    const production = asPositiveNumber(variant.productionW);
    const layoutModules = countModulesInLayout(variant.modulesLayout);
    return modules > 0 || production > 0 || layoutModules > 0;
}

function isViableVariant(
    variant: AltLayoutVariantRow,
    args: {
        ignoreSolarAccess: boolean;
        relaxValidation: boolean;
    },
): boolean {
    if (!hasLayoutQuantityData(variant)) return false;
    if (args.ignoreSolarAccess || args.relaxValidation) return true;
    const solar = asOptionalMetric(variant.solarAccess);
    return solar != null;
}

/** Live AI run: hide variants that are the same physical layout as the category main card. */
function isDuplicateOfCategoryMainOnRun(
    category: string,
    variant: AltLayoutVariantRow,
    categoryMain: CategoryLayoutCandidate | undefined,
): boolean {
    if (!categoryMain) return false;
    if (categoryMain.variantId && variant.id && categoryMain.variantId === variant.id) return true;

    const candidate = variantToCandidate(category, variant);
    if (physicalLayoutSignature(categoryMain) === physicalLayoutSignature(candidate)) return true;
    return candidateLayoutSignature(categoryMain) === candidateLayoutSignature(candidate);
}

/** Cache / DB: only hide the exact stored winner row (by id). Metric-only dedup false-filters alts. */
function isDuplicateOfCategoryMainFromCache(
    variant: AltLayoutVariantRow,
    categoryMain: CategoryLayoutCandidate | undefined,
): boolean {
    if (!categoryMain?.variantId || !variant.id) return false;
    return categoryMain.variantId === variant.id;
}

function dedupeByLayoutSignature(category: string, variants: AltLayoutVariantRow[]): AltLayoutVariantRow[] {
    const seen = new Set<string>();
    const out: AltLayoutVariantRow[] = [];
    for (const variant of variants) {
        const sig = candidateLayoutSignature(variantToCandidate(category, variant));
        if (seen.has(sig)) continue;
        seen.add(sig);
        out.push(variant);
    }
    return out;
}

/**
 * Filter layout variants for the More Options / More Layouts panels.
 * Global rows (no category) skip category-main dedup entirely.
 */
export function filterAltLayoutDisplayVariants(args: {
    category: string | null | undefined;
    variants: AltLayoutVariantRow[];
    categoryMainLayoutByCategory?: Record<string, CategoryLayoutCandidate>;
    aiDesignsDataSource?: AiDesignsDataSource | null;
    ignoreSolarAccessForOneDrawDefaultMaxfill?: boolean;
    layoutModulesLayoutByKey?: Record<string, SavedRoofModulesLayout | undefined>;
    layoutProductionByKey?: Record<string, number | undefined>;
    layoutModulesByKey?: Record<string, number | undefined>;
    layoutMetricsByKey?: Record<string, unknown>;
}): AltLayoutVariantRow[] {
    const {
        category,
        variants,
        categoryMainLayoutByCategory = {},
        aiDesignsDataSource = null,
        ignoreSolarAccessForOneDrawDefaultMaxfill = false,
        layoutModulesLayoutByKey,
        layoutProductionByKey,
        layoutModulesByKey,
        layoutMetricsByKey,
    } = args;

    if (!variants.length) return [];

    const relaxValidation = aiDesignsDataSource !== 'run';
    const fromPersistedCatalog = aiDesignsDataSource === 'cache' || aiDesignsDataSource === 'db';
    const viabilityArgs = {
        ignoreSolarAccess: Boolean(
            ignoreSolarAccessForOneDrawDefaultMaxfill ||
                (category && canonicalCategoryName(category) === canonicalCategoryName('oneDRAW Default Maxfill')),
        ),
        relaxValidation,
    };

    const resolvedVariants = variants.map((variant) =>
        resolveAltLayoutVariantMetrics({
            category,
            variant,
            layoutModulesLayoutByKey,
            layoutProductionByKey,
            layoutModulesByKey,
            layoutMetricsByKey,
        }),
    );

    const viable = resolvedVariants.filter((variant) => isViableVariant(variant, viabilityArgs));
    if (!viable.length) return [];

    // Global "More layouts" — no category main card to dedupe against.
    if (!category) {
        return dedupeByLayoutSignature('global', viable);
    }

    const categoryMain = resolveCategoryMain(category, categoryMainLayoutByCategory);
    const isOneDrawDefaultCategory =
        canonicalCategoryName(category) === canonicalCategoryName('oneDRAW Default Maxfill');

    const filtered = viable.filter((variant) => {
        const isMixedVariant =
            typeof variant.label === 'string' && variant.label.toLowerCase().includes('mixed');
        if (fromPersistedCatalog) {
            if (isDuplicateOfCategoryMainFromCache(variant, categoryMain)) return false;
            return true;
        }
        if (
            isDuplicateOfCategoryMainOnRun(category, variant, categoryMain) &&
            !(isOneDrawDefaultCategory && isMixedVariant)
        ) {
            return false;
        }
        return true;
    });

    const deduped = dedupeByLayoutSignature(category, filtered.length ? filtered : viable);

    // Persisted catalog: if metric dedup would hide everything, show all viable alts anyway.
    if (fromPersistedCatalog && deduped.length === 0 && viable.length > 0) {
        return dedupeByLayoutSignature(
            category,
            viable.filter((variant) => !isDuplicateOfCategoryMainFromCache(variant, categoryMain)),
        );
    }

    return deduped;
}

/** True when a category has at least one More Options row to render (excluding the main-card winner). */
export function hasDisplayableCategoryLayoutVariants(args: {
    category: string;
    variantsByCategory: Record<string, unknown[]>;
    categoryMainLayoutByCategory?: Record<string, CategoryLayoutCandidate>;
    aiDesignsDataSource?: AiDesignsDataSource | null;
    layoutModulesLayoutByKey?: Record<string, SavedRoofModulesLayout | undefined>;
    layoutProductionByKey?: Record<string, number | undefined>;
    layoutModulesByKey?: Record<string, number | undefined>;
    layoutMetricsByKey?: Record<string, unknown>;
}): boolean {
    const variants = resolveVariantsForCategory(args.category, args.variantsByCategory);
    if (!variants.length) return false;
    return (
        filterAltLayoutDisplayVariants({
            category: args.category,
            variants,
            categoryMainLayoutByCategory: args.categoryMainLayoutByCategory ?? {},
            aiDesignsDataSource: args.aiDesignsDataSource ?? 'cache',
            layoutModulesLayoutByKey: args.layoutModulesLayoutByKey,
            layoutProductionByKey: args.layoutProductionByKey,
            layoutModulesByKey: args.layoutModulesByKey,
            layoutMetricsByKey: args.layoutMetricsByKey,
        }).length > 0
    );
}
